/**
 * Money — amount in the base currency (USD, minor units) with its VES
 * equivalent: `$25.00 / Bs. 912,50 @ 36,50`.
 *
 * **The client does NOT convert.** The bolivar arrives already converted from the server
 * (`*VesMinor` fields), which is the one holding the rate, the rounding policy and the
 * accounting responsibility. This component used to multiply by the rate in
 * MAJOR units and let `Intl` round, while the server rounded
 * to a minor unit: two roundings for the same amount, and the same number could
 * print differently depending on the screen. Worse still, the
 * `totalDebtVesMinor` / `savingsVesMinor` / `totalVesMinor` fields the server ALREADY
 * computed were consumed by nobody.
 *
 * The VES part only appears if the server sent it: a conversion is never
 * invented. If an amount needs bolivars and the server doesn't offer them, the
 * field gets added on the SERVER — the conversion isn't reintroduced here.
 */
import { type Component, Show } from "solid-js";

interface MoneyProps {
  /** Amount in USD, minor units (cents). */
  usdMinor: number;
  /**
   * The SAME amount in VES, minor units, exactly as the server calculated it.
   * Absent/`null` = no captured rate: only USD is shown.
   */
  vesMinor?: number | null;
  /** Current VES-per-USD rate — only for the `@ 36,50` label. */
  vesPerUsd?: number | null;
  /** Show the `@ 36,50` rate line. Defaults to true if there's a rate. */
  showRate?: boolean;
  locale?: string;
  class?: string;
}

const MINOR = 100;

const format = (minor: number, currency: string, locale: string): string => {
  try {
    return new Intl.NumberFormat(locale, { style: "currency", currency }).format(minor / MINOR);
  } catch {
    // Currency not recognized by Intl: readable fallback.
    return `${(minor / MINOR).toFixed(2)} ${currency}`;
  }
};

export const Money: Component<MoneyProps> = (props) => {
  const locale = () => props.locale ?? "es-VE";
  const ves = () => {
    const v = props.vesMinor;
    return v === null || v === undefined || !Number.isFinite(v) ? null : v;
  };

  const rateLabel = () => {
    const r = props.vesPerUsd;
    if (r === null || r === undefined || !Number.isFinite(r) || r <= 0) {
      return null;
    }
    return new Intl.NumberFormat(locale(), {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(r);
  };

  return (
    <span class={props.class} data-testid="money">
      <span class="font-semibold tabular-nums">{format(props.usdMinor, "USD", locale())}</span>
      <Show when={ves() !== null}>
        <span class="text-muted-foreground"> / </span>
        <span class="tabular-nums text-muted-foreground" data-testid="money-ves">
          {format(ves() ?? 0, "VES", locale())}
        </span>
      </Show>
      <Show when={(props.showRate ?? true) && ves() !== null && rateLabel()}>
        <span class="text-xs text-muted-foreground"> @ {rateLabel()}</span>
      </Show>
    </span>
  );
};
